import { MobilePageLayout } from '../../layouts/mobile/MobilePageLayout';
import { useShowMobileLayout } from '../../utils/LayoutUtils';
import { useIsChairside } from '../../utils/chairside.hooks';
import { PatientDetailMain } from './components/patient-detail/PatientDetailMain.graphql';
import { PatientDetailSidebar } from './components/patient-detail/PatientDetailSidebar';
import { PatientDisplayUtils } from './utils/PatientDisplay.utils';
import { useQuery } from '@apollo/client';
import type {
    FragmentType,
    PatientDetailMainPatient_FragmentFragmentDoc,
    PatientDetailMainScan_FragmentFragmentDoc,
} from '@orthly/graphql-inline-react';
import { graphql } from '@orthly/graphql-inline-react';
import { LoadBlocker } from '@orthly/ui';
import { styled, useScreenIsMobile } from '@orthly/ui-primitives';
import React from 'react';
import { useRouteMatch } from 'react-router-dom';

const Layout = styled('div')(({ theme }) => ({
    display: 'grid',
    height: '100%',
    width: '100%',
    gridTemplateColumns: `4fr minmax(250px, 1fr)`,
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: `1fr`,
    },
}));

const PatientDetailRoot_Query = graphql(`
    query PatientDetailRoot_Query($patient_id: String!) {
        details: getPatientDetails(patient_id: $patient_id) {
            patient {
                ...PatientDetailMainPatient_Fragment
                first_name
                last_name
            }
            scans {
                ...PatientDetailMainScan_Fragment
            }
            last_visit
        }
    }
`);

const usePatientDetails = () => {
    const patient_id = useRouteMatch<{ patientId: string }>()?.params.patientId;

    return useQuery(PatientDetailRoot_Query, {
        variables: { patient_id },
    });
};

interface PatientDetailBaseProps {
    lastVisit?: string | null;
    patientMainFragment: FragmentType<typeof PatientDetailMainPatient_FragmentFragmentDoc>;
    scanMainFragments: FragmentType<typeof PatientDetailMainScan_FragmentFragmentDoc>[];
}

const PatientDetailBase: React.VFC<PatientDetailBaseProps> = props => {
    const { patientMainFragment, scanMainFragments, lastVisit } = props;

    return (
        <Layout>
            <PatientDetailMain patientMainFragment={patientMainFragment} scanMainFragments={scanMainFragments} />
            <PatientDetailSidebar lastVisit={lastVisit} cases={scanMainFragments.length} />
        </Layout>
    );
};

export const PatientDetailRoot: React.VFC = () => {
    const { data, loading } = usePatientDetails();

    const chairside = useIsChairside();
    const isMobileLayout = useShowMobileLayout();
    const isMobile = useScreenIsMobile();

    if (loading || !data) {
        return <LoadBlocker blocking={loading} ContainerProps={{ style: { height: '100%', width: '100%' } }} />;
    }

    const baseProps: PatientDetailBaseProps = {
        lastVisit: data.details.last_visit,
        patientMainFragment: data.details.patient,
        scanMainFragments: data.details.scans,
    };

    if (isMobileLayout && !chairside) {
        return (
            <MobilePageLayout
                titleHasBackButton
                showHeaderAsSecondaryHeader={!isMobile}
                title={PatientDisplayUtils.getPatientName(data.details.patient)}
            >
                <PatientDetailBase {...baseProps} />
            </MobilePageLayout>
        );
    }

    return <PatientDetailBase {...baseProps} />;
};
